import { Button, Flex, Text } from "@suns/design-system";
import bigBoards from "./big-board-ranks.json";
import { useAccount, useAsync } from "@/shared/hooks";
import { playersLoader } from "./loaders/player-loader";
import { useMemo, useState } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
// eslint-disable-next-line no-restricted-imports -- DO NOT COPY THIS LINE. See docs/lints.md.
import { PlayerRow } from "@suns/api/generated-client/apollo";
import { TeamLogo } from "@/components";
import { DataTable } from "@suns/design-system/src/components/DataTable/DataTable";
import { useNavigate } from "react-router-dom";
import { getLabel } from "@/shared/utils";

type DraftPlayerRow = PlayerRow & {
  scoutRank: number;
  consensus: {
    rank: number | null;
    min: number | null;
    max: number | null;
  };
};

const columns: ColumnDef<DraftPlayerRow>[] = [
  {
    header: "Rank",
    accessorKey: "scoutRank",
    cell: (cell) => {
      const rank = cell.getValue<number | null>();
      return <Text heading>{rank !== null ? rank : "--"}</Text>;
    },
    enableSorting: true,
    sortingFn: "alphanumeric",
    sortDescFirst: false,
  },
  {
    header: "Player",
    accessorKey: "name",
    accessorFn: (row: DraftPlayerRow) => row.lastName + ", " + row.firstName,
    enableSorting: true,
    sortingFn: "text",
  },
  {
    header: "Team",
    accessorKey: "currentTeam",
    cell: (cell) => {
      const teams = cell.row.original.currentTeams;
      return (
        <Flex direction="right" align="center" justify="center" gap="sm">
          {teams?.map((team, i) => (
            <Flex direction="right" align="center" gap="xs" key={team.id}>
              <TeamLogo
                src={team.image}
                nbaTeamId={team.nbaId || team.id}
                size="3xs"
              />
              <Text size="sm" key={team.id} className="text-nowrap">
                {team.code ? team.code : team.name!.slice(0, 3).toUpperCase()}
                {i < teams.length - 1 ? "," : ""}
              </Text>
            </Flex>
          ))}
        </Flex>
      );
    },
    enableSorting: false,
    sortDescFirst: false,
    sortingFn: "text",
    accessorFn: (row: PlayerRow) =>
      row.currentTeams.map((team) => team.code).join(", "),
  },
  {
    header: "Position",
    accessorKey: "position",
    enableSorting: false,
    sortingFn: "text",
    accessorFn: (row: PlayerRow) => {
      const position = row.metadata?.[0]?.hybridPosition;
      return position ? getLabel(position) : row.position;
    },
  },
  {
    header: "Analytics Rank",
    accessorKey: "analyticsRank",
    enableSorting: true,
    sortingFn: "alphanumeric",
    sortDescFirst: false,
    cell: (cell) => {
      const rank = cell.getValue<number | null>();
      return <Text>{rank !== null ? rank : "--"}</Text>;
    },
  },
  {
    header: "Consensus Rank",
    accessorKey: "consensusRank",
    enableSorting: false,
    cell: (cell) => {
      const { consensus } = cell.row.original;
      if (!consensus || consensus.rank === null) {
        return <Text>--</Text>;
      }
      return (
        <Text>
          {consensus.min === consensus.max
            ? consensus.min
            : `${consensus.min}-${consensus.max}`}
        </Text>
      );
    },
  },
];

export function PlayersDraftBigBoard() {
  const { hasDeveloperAccess } = useAccount();
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "scoutRank", desc: false },
  ]);

  const allPlayerIds = new Set();

  for (const board of Object.values(bigBoards)) {
    for (const rankedPlayer of board) {
      allPlayerIds.add(rankedPlayer.playerId);
    }
  }

  const { response, loading } = useAsync(playersLoader, {
    offset: 0,
    limit: 100,
    "player.in": Array.from(allPlayerIds).map(String),
  });

  const players = useMemo(() => {
    return response?.players
      .map((player) => {
        const scoutRank =
          bigBoards.scoutBigBoard.findIndex(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          ) + 1;

        const consensusPlayer = bigBoards.consensusBigBoard.find(
          (rankedPlayer) => rankedPlayer.playerId === player.id
        );

        const consensusRank =
          bigBoards.consensusBigBoard.findIndex(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          ) + 1;

        const analyticsRank =
          bigBoards.analyticsBigBoard.find(
            (rankedPlayer) => rankedPlayer.playerId === player.id
          )?.rank ?? 0;

        return {
          ...player,
          scoutRank: scoutRank > 0 ? scoutRank : null,
          analyticsRank: analyticsRank > 0 ? analyticsRank : null,
          consensus: {
            rank: consensusRank > 0 ? consensusRank : null,
            min: consensusPlayer?.min ?? null,
            max: consensusPlayer?.max ?? null,
          },
        };
      })
      .sort((player1, player2) => {
        let res = 0;
        if (sorting[0]?.id === "name") {
          res = player1.lastName.localeCompare(player2.lastName);
          if (res == 0)
            res = player1.firstName.localeCompare(player2.firstName);
        } else if (sorting[0]?.id === "scoutRank") {
          if (player1.scoutRank === null) res = 1;
          else if (player2.scoutRank === null) res = -1;
          else res = player1.scoutRank - player2.scoutRank;
        } else if (sorting[0]?.id === "analyticsRank") {
          if (player1.analyticsRank === null) res = 1;
          else if (player2.analyticsRank === null) res = -1;
          else res = player1.analyticsRank - player2.analyticsRank;
        } else if (sorting[0]?.id === "consensusRank") {
          if (player1.consensus === null) res = 1;
          else if (player2.consensus === null) res = -1;
          else {
            const rank1 = player1.consensus?.rank ?? 0;
            const rank2 = player2.consensus?.rank ?? 0;
            res = rank1 - rank2;
          }
        }
        return sorting[0]?.desc ? -res : res;
      });
  }, [response, sorting]);

  return (
    <Flex direction="down" gap="md">
      <Flex
        direction="right"
        align="center"
        gap="sm"
        justify="between"
        className="pt-4 max-sm:flex-col max-sm:items-start lg:pt-0"
      >
        <Text size="xl" heading>
          2025 NBA Draft Big Board
        </Text>
        <div>
          <Flex direction="right" gap="sm">
            <Button variant="outline" onClick={() => navigate("print-table")}>
              Print Big Board
            </Button>
            {hasDeveloperAccess && (
              <Button variant="outline" onClick={() => navigate("print")}>
                Print All Players
              </Button>
            )}
          </Flex>
        </div>
      </Flex>
      <DataTable
        columns={columns}
        data={players as DraftPlayerRow[]}
        loading={loading}
        sorting={sorting}
        setSorting={(newSorting) => {
          const newSort =
            typeof newSorting === "function" ? newSorting(sorting) : newSorting;

          setSorting(newSort);
        }}
        onRowClicked={(row) => {
          navigate(`/players/${row.original.id}`);
        }}
      />
    </Flex>
  );
}
